import { FC } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { Restaurant } from '@/types/models/Restaurant';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { passToRouteUrlParams } from '@/utils/passToRouteUrlParams.ts';
import Routes from '@/router/Routes.ts';
import RouteParam from '@/_constants/RouteParam.ts';
import { timeStringToDayjsWithTz } from '@/utils/dayjsToTimeString.ts';

type TableRow = Restaurant;

type RestaurantsTableProps = {
  rows: TableRow[];
  selectable?: boolean;
  onSelectedChange?: (selectedElements: number[]) => void;
};

const RestaurantsTable: FC<PropsWithClassName<RestaurantsTableProps>> = ({
  rows,
  className,
  onSelectedChange,
  selectable = false,
}) => {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('tableLabel.name'), width: 200, cellClassName: 'flex items-center' },
    { field: 'address', headerName: t('tableLabel.address'), width: 300, cellClassName: 'flex items-center' },
    { field: 'phone', headerName: t('tableLabel.phone'), width: 150, cellClassName: 'flex items-center' },
    {
      field: 'redirect_phone',
      headerName: t('tableLabel.redirectPhone'),
      width: 150,
      cellClassName: 'flex items-center',
    },
    {
      field: 'created_at',
      headerName: t('tableLabel.accessionDate'),
      width: 150,
      cellClassName: 'flex items-center',
      valueGetter: (_value, row) => dayjs(row.created_at).format('MM/DD/YY'),
    },
    {
      field: 'timezone',
      headerName: t('tableLabel.timezone'),
      width: 150,
      cellClassName: 'flex items-center',
      valueGetter: (_value, row) => row.timezone ?? '-',
    },
    {
      field: 'weekday_hours',
      headerName: t('tableLabel.weekdayHours'),
      width: 150,
      cellClassName: 'flex items-center',
      valueGetter: (_value, row) =>
        !!row.weekday_hours_from && !!row.weekday_hours_to
          ? `${timeStringToDayjsWithTz(row.weekday_hours_from, row.timezone ?? 'UTC')?.format('HH:mm') ?? '|'} - ${timeStringToDayjsWithTz(row.weekday_hours_to, row.timezone ?? 'UTC')?.format('HH:mm') ?? '|'}`
          : '-',
    },
    {
      field: 'weekend_hours',
      headerName: t('tableLabel.weekendHours'),
      width: 150,
      cellClassName: 'flex items-center',
      valueGetter: (_value, row) =>
        !!row.weekend_hours_from && !!row.weekend_hours_to
          ? `${timeStringToDayjsWithTz(row.weekend_hours_from, row.timezone ?? 'UTC')?.format('HH:mm') ?? '|'} - ${timeStringToDayjsWithTz(row.weekend_hours_to, row.timezone ?? 'UTC')?.format('HH:mm') ?? '|'}`
          : '-',
    },
    {
      field: 'bot_enabled',
      headerName: t('tableLabel.botEnabled'),
      width: 150,
      cellClassName: 'flex items-center',
      valueGetter: (_value, row) => (row.bot_enabled ? 'on' : 'off'),
    },
    {
      field: 'reservations',
      headerName: t('tableLabel.reservations'),
      width: 250,
      cellClassName: 'flex items-center',
      renderCell: (params) => (
        <Link
          className="text-primary-400"
          to={passToRouteUrlParams(Routes.ADMIN__RESTAURANTS__RESERVATIONS, {
            [RouteParam.RESTAURANT_ID]: params.row.id,
          })}
        >
          {t('components.restaurantsTable.showReservations')}
        </Link>
      ),
      valueGetter: (_value, row) => row.id,
    },
  ];

  const paginationModel = { page: 0, pageSize: 10 };

  return (
    <Paper sx={{ width: '100%' }} className={className}>
      <DataGrid
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        getRowHeight={() => 'auto'}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[10, 20, 50, 100]}
        rowSelection={selectable}
        checkboxSelection={selectable}
        disableMultipleRowSelection={true}
        disableRowSelectionOnClick={true}
        onRowSelectionModelChange={(selectedIds: GridRowSelectionModel) => {
          if (onSelectedChange) {
            onSelectedChange(selectedIds.map((id) => id as number) ?? []);
          }
        }}
      />
    </Paper>
  );
};

export default RestaurantsTable;
